import { Children, Fragment, ReactNode, isValidElement } from 'react'
import TablePagination from './TablePagination'

type Props = {
  ariaLabel: string
  caption: string
  navAriaLabel: string
  emptyText: string
  isEmpty: boolean
  loading: boolean
  error: string | null
  onRetry: () => void
  page: number
  pageSize: number
  totalElements: number
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
  headerCells: ReactNode
  bodyRows: ReactNode
}

// Header cells are typically passed as a fragment; unwrap it so we count the actual <th>s.
const countHeaderColumns = (headerCells: ReactNode): number => {
  if (isValidElement(headerCells) && headerCells.type === Fragment) {
    return Children.count(headerCells.props.children)
  }
  return Children.count(headerCells)
}

const ReviewsTableShell = ({
  ariaLabel,
  caption,
  navAriaLabel,
  emptyText,
  isEmpty,
  loading,
  error,
  onRetry,
  page,
  pageSize,
  totalElements,
  onPageChange,
  onPageSizeChange,
  headerCells,
  bodyRows,
}: Props): JSX.Element => {
  const colCount = countHeaderColumns(headerCells)

  return (
    <div className="rounded-2xl bg-white p-8 shadow-[0_0_20px_5px_rgba(0,0,0,0.08)]">
      {error && (
        <div
          role="alert"
          className="mb-4 flex items-center justify-between rounded-lg border border-light-red bg-light-red px-4 py-3 text-xl text-error-red"
        >
          <span>{error}</span>
          <button onClick={onRetry} className="ml-4 font-medium underline hover:no-underline">
            Retry
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-xl" aria-label={ariaLabel}>
          <caption className="sr-only">{caption}</caption>
          <thead>
            <tr className="border-b border-soft-gray">{headerCells}</tr>
          </thead>
          <tbody
            aria-live="polite"
            aria-busy={loading}
            className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}
          >
            {isEmpty ? (
              <tr>
                <td colSpan={colCount} className="py-12 text-center text-stone-gray">
                  {loading ? 'Loading…' : emptyText}
                </td>
              </tr>
            ) : (
              bodyRows
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        page={page}
        pageSize={pageSize}
        totalElements={totalElements}
        navAriaLabel={navAriaLabel}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
    </div>
  )
}

export default ReviewsTableShell
